import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiBlob } from '../lib/api.js';

/**
 * Opens a server-rendered PDF (Phase 2D). A plain link cannot carry the
 * bearer token, so the bytes are fetched with it and shown from a blob URL
 * in a new tab — which is also what makes "download" work on a phone.
 */
export function PdfButton({ path, filename, testId }: { path: string; filename: string; testId: string }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const open = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const url = URL.createObjectURL(await apiBlob(path));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={() => void open()}
        disabled={busy}
        className="rounded-lg px-3 py-1.5 text-sm text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-100 disabled:opacity-40"
        data-testid={testId}
      >
        {busy ? t('pdf.preparing') : t('pdf.open')}
      </button>
      {failed && <span className="text-xs text-red-700" role="alert">{t('pdf.failed')}</span>}
    </span>
  );
}
